import { useEffect, useState } from "react";
import { Settings, CalendarDays, Landmark, Building2, ShieldCheck, Check, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { ROLE_LABELS } from "@/context/AuthContext";
import { FestgeldStatusBadge } from "@/components/shared/Shared";

function PermissionsMatrix() {
  const { t, tt } = useLang();
  const [matrix, setMatrix] = useState(null);

  useEffect(() => {
    api.get("/admin/permissions-matrix").then((r) => setMatrix(r.data)).catch(() => setMatrix({ capabilities: [], roles: [] }));
  }, []);

  if (!matrix) return <Skeleton className="h-64 rounded-[16px]" />;

  return (
    <Card className="rounded-[16px]" data-testid="permissions-matrix-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#BFA76A]" /> {t("Rollen- & Rechte-Matrix", "Roles & Permissions Matrix")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("Verbindliche \u00dcbersicht, welche Rolle welche Aktionen ausf\u00fchren darf. Wird serverseitig durchgesetzt.", "Binding overview of which role may perform which actions. Enforced server-side.")}</p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table data-testid="permissions-matrix-table">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">{t("Berechtigung", "Capability")}</TableHead>
              {matrix.roles.map((r) => (
                <TableHead key={r} className="text-center text-[11px] whitespace-nowrap">{tt(ROLE_LABELS[r]) || r}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {matrix.capabilities.map((cap) => (
              <TableRow key={cap.id} data-testid={`matrix-row-${cap.id}`}>
                <TableCell className="text-xs font-medium">{t(cap.label_de, cap.label_en)}</TableCell>
                {matrix.roles.map((r) => (
                  <TableCell key={r} className="text-center">
                    {cap.roles.includes(r) ? (
                      <Check className="h-4 w-4 text-emerald-600 mx-auto" data-testid={`matrix-${cap.id}-${r}-yes`} />
                    ) : (
                      <Minus className="h-3.5 w-3.5 text-muted-foreground/30 mx-auto" />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function AdminSettings() {
  const { t } = useLang();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/admin/settings", {
        calendly_url: settings.calendly_url,
        festgeld_status: settings.festgeld_status,
        immobilien_enabled: settings.immobilien_enabled,
      });
      setSettings(res.data);
      toast.success(t("Einstellungen gespeichert", "Settings saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Fehler beim Speichern", "Error saving"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-80 rounded-[16px] max-w-2xl" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{t("System-Einstellungen", "System Settings")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("Website-Konfiguration ohne Deployment \u00e4ndern", "Change website configuration without deployment")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl">
        <Card className="rounded-[16px]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-[#BFA76A]" /> {t("Terminbuchung (Calendly)", "Appointment booking (Calendly)")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Calendly URL</Label>
              <Input value={settings.calendly_url || ""} onChange={(e) => setSettings({ ...settings, calendly_url: e.target.value })} className="rounded-[10px]" data-testid="settings-calendly-input" />
              <p className="text-xs text-muted-foreground">{t("Wird auf der \u00f6ffentlichen Terminseite eingebettet.", "Embedded on the public booking page.")}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[16px]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Landmark className="h-4 w-4 text-[#BFA76A]" /> {t("Festgeld-Statusanzeige", "Fixed-term status display")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("\u00d6ffentlicher Status", "Public status")}</Label>
              <Select value={settings.festgeld_status} onValueChange={(v) => setSettings({ ...settings, festgeld_status: v })}>
                <SelectTrigger className="rounded-[10px]" data-testid="settings-festgeld-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_pruefung">{t("Aktuell in Pr\u00fcfung", "Currently under review")}</SelectItem>
                  <SelectItem value="begrenzt">{t("Begrenzte Verf\u00fcgbarkeit", "Limited availability")}</SelectItem>
                  <SelectItem value="keine_anzeige">{t("Keine \u00f6ffentliche Konditionsanzeige", "No public rate disclosure")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2">{t("Vorschau:", "Preview:")}</p>
              <FestgeldStatusBadge status={settings.festgeld_status} />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[16px]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#BFA76A]" /> {t("Produktbereiche", "Product areas")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-center justify-between text-sm">
              <div>
                <p className="font-medium">{t("Immobilienbereich aktiv", "Real estate section active")}</p>
                <p className="text-xs text-muted-foreground">{t("Steuert Hinweise auf der Immobilienseite.", "Controls notices on the real estate page.")}</p>
              </div>
              <Switch checked={settings.immobilien_enabled} onCheckedChange={(v) => setSettings({ ...settings, immobilien_enabled: v })} data-testid="settings-immobilien-switch" />
            </label>
          </CardContent>
        </Card>
      </div>

      <Button onClick={save} disabled={saving} className="rounded-[12px] bg-[#0B1220]" data-testid="settings-save-button">
        {saving ? t("Speichert\u2026", "Saving\u2026") : t("Einstellungen speichern", "Save settings")}
      </Button>

      <div className="max-w-5xl">
        <PermissionsMatrix />
      </div>
    </div>
  );
}
